import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import { Monitor, ArrowLeft } from "lucide-react";
import { useForgotPassword } from "../../api/auth";

export function ForgotPasswordPage() {
  const forgot = useForgotPassword();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    // Account-enumeration defence: whether the email exists or not,
    // the backend returns 202 with the same message. We show the same
    // confirmation regardless of mutation success/failure so a network
    // outage also doesn't leak.
    try {
      await forgot.mutateAsync({ email });
    } catch {
      /* intentionally ignored — see comment above */
    }
    setSubmitted(true);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-riot-navy-900">
      <div className="w-full max-w-sm">
        <div className="rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800 px-8 py-10 shadow-sm">
          <div className="mb-6 flex flex-col items-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-riot-navy-800">
              <Monitor size={24} className="text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Reset Password</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-riot-navy-300">We'll send you a reset code</p>
          </div>

          {submitted ? (
            <div className="text-center">
              <div className="mb-4 rounded-md bg-green-50 px-4 py-3 text-sm text-green-700 dark:bg-green-950/30 dark:text-green-300">
                If an account exists for <strong>{email}</strong>, a reset code has been sent.
              </div>
              <Link to="/reset-password" className="text-sm font-medium text-riot-gold-600 hover:underline dark:text-riot-gold-400">Enter reset code</Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">Email</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500" />
              </label>
              <button type="submit" className="w-full rounded-md bg-riot-gold-400 px-4 py-2.5 text-sm font-medium text-riot-navy-900 shadow-sm hover:bg-riot-gold-500 active:bg-riot-gold-600 focus:outline-none focus:ring-2 focus:ring-riot-gold-400 focus:ring-offset-2">Send Reset Code</button>
            </form>
          )}

          <p className="mt-6 text-center">
            <Link to="/sign-in" className="flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-riot-navy-300 dark:hover:text-white">
              <ArrowLeft size={12} /> Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
